import React, { useState, useMemo } from "react";
import { TextField, Container, Autocomplete } from "@mui/material";
import SuccessModal from "../successModal";
import ThankYouDialog from "../thankyouModal";
import { API_URL } from "@src/config/apiconfig";
import {
  initialFormState,
  productOptions,
  validationRules,
  validateAllFields,
} from "./formData";
import indiaStateCities from "@src/constants/indiaStateCities.json";

import "./index.scss";

const Form = () => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openThankYouModal, setOpenThankYouModal] = useState(false);
  const [warrantyChecked, setWarrantyChecked] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState(null);

  // Build state list and city list from JSON
  const stateList = useMemo(
    () => indiaStateCities.india_states_cities.map((s) => s.state),
    [],
  );

  const cityList = useMemo(() => {
    if (!form.state) return [];
    const found = indiaStateCities.india_states_cities.find(
      (s) => s.state === form.state,
    );
    return found ? found.cities : [];
  }, [form.state]);

  const handleWarrantyCheckbox = (event) => {
    setWarrantyChecked(event.target.checked);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Handle mobile number - only digits, max 10
    if (name === "mobile") {
      const onlyNums = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, [name]: onlyNums }));

      if (validationRules[name]) {
        const isValid = validationRules[name](onlyNums);
        setErrors((err) => ({
          ...err,
          [name]: isValid === true ? undefined : isValid,
        }));
      }
      return;
    }

    // Handle pincode - only digits, max 6
    if (name === "pincode") {
      const onlyNums = value.replace(/\D/g, "").slice(0, 6);
      setForm((prev) => ({ ...prev, [name]: onlyNums }));

      if (validationRules[name]) {
        const isValid = validationRules[name](onlyNums);
        setErrors((err) => ({
          ...err,
          [name]: isValid === true ? undefined : isValid,
        }));
      }
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    // Validate field
    if (validationRules[name]) {
      const isValid = validationRules[name](value);
      setErrors((err) => ({
        ...err,
        [name]: isValid === true ? undefined : isValid,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (validationRules[name]) {
      const isValid = validationRules[name](value);
      setErrors((err) => ({
        ...err,
        [name]: isValid === true ? undefined : isValid,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg(null);

    const newErrors = validateAllFields(form, errors);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(API_URL.SAVE_FORM_DATA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      let responseBody = null;
      try {
        responseBody = await response.json();
      } catch {}

      if (response.ok && responseBody && responseBody.status === "success") {
        setSubmitMsg(null);
        setErrors({});
        setOpenSuccessModal(true);
        setSubmittedFormData(form);
        setForm(initialFormState);
      } else if (response.status === 405) {
        setSubmitMsg(
          "Submission failed: The server returned 405 Method Not Allowed. Please ensure that the server is configured to accept POST requests at this endpoint.",
        );
      } else if (response.status === 0) {
        setSubmitMsg(
          "Submission failed: Network or CORS error detected. Even though CORS headers are set, there may be a network issue, server-side misconfiguration, or a problem with the endpoint URL. Please verify the server is running, accessible, and CORS headers are returned for OPTIONS and POST.",
        );
      } else {
        setSubmitMsg("Submission failed. Please try again.");
      }
    } catch (error) {
      setSubmitMsg(
        "A network or CORS error has occurred, even though CORS access is allowed on the API. Please double-check that your API endpoint is correct, the server is reachable, and the CORS headers are set for all responses, including OPTIONS preflight. If the issue continues, try clearing browser cache or testing from a different network.",
      );
    }

    setSubmitting(false);
  };

  const handleWarrantyUpdate = async () => {
    if (!warrantyChecked || !submittedFormData) return;

    try {
      await fetch(API_URL.SAVE_EXTENDED_WARRANTY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...submittedFormData,
          consent: 1,
        }),
      });
    } catch {}
  };

  return (
    <>
      <Container maxWidth={false} sx={{ p: 0 }} className="main-form-container">
        <form className="form-container" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-half">
              <TextField
                id="name"
                name="name"
                label="Name"
                variant="outlined"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.name}
                helperText={errors.name || ""}
                autoComplete="off"
                size="medium"
              />
            </div>
            <div className="form-half">
              <TextField
                id="email"
                name="email"
                label="Email ID"
                variant="outlined"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.email}
                helperText={errors.email || ""}
                autoComplete="off"
                size="medium"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-half">
              <TextField
                id="mobile"
                name="mobile"
                label="Mobile Number"
                variant="outlined"
                value={form.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.mobile}
                helperText={errors.mobile || ""}
                autoComplete="off"
                size="medium"
              />
            </div>
            <div className="form-half">
              <Autocomplete
                id="product"
                options={productOptions}
                getOptionLabel={(option) => option.label}
                value={
                  productOptions.find((opt) => opt.value === form.product) ||
                  null
                }
                onChange={(_, newValue) => {
                  handleChange({
                    target: {
                      name: "product",
                      value: newValue ? newValue.value : "",
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    variant="outlined"
                    name="product"
                    error={!!errors.product}
                    helperText={errors.product || ""}
                    size="medium"
                    onBlur={handleBlur}
                    autoComplete="off"
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                fullWidth
                clearOnEscape
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-half">
              <TextField
                id="serial"
                name="serial"
                label="Product Serial Number"
                variant="outlined"
                value={form.serial}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.serial}
                helperText={errors.serial || ""}
                autoComplete="off"
                size="medium"
              />
            </div>
            <div className="form-half">
              <TextField
                id="invoiceNumber"
                name="invoiceNumber"
                label="Invoice Number"
                variant="outlined"
                value={form.invoiceNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.invoiceNumber}
                helperText={errors.invoiceNumber || ""}
                autoComplete="off"
                size="medium"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-half">
              <TextField
                id="purchaseDate"
                name="purchaseDate"
                label="Date of Purchase"
                variant="outlined"
                type="date"
                value={form.purchaseDate}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.purchaseDate}
                helperText={errors.purchaseDate || ""}
                slotProps={{ inputLabel: { shrink: true } }}
                size="medium"
              />
            </div>
            <div className="form-half">
              <TextField
                id="pincode"
                name="pincode"
                label="City Pin code"
                variant="outlined"
                value={form.pincode}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                error={!!errors.pincode}
                helperText={errors.pincode || ""}
                autoComplete="off"
                size="medium"
                slotProps={{
                  htmlInput: {
                    maxLength: 6,
                    minLength: 6,
                    inputMode: "numeric",
                    pattern: "[0-9]{6}",
                  },
                }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-half">
              <Autocomplete
                id="state"
                options={stateList}
                getOptionLabel={(option) => option}
                value={form.state || null}
                onChange={(_, newValue) => {
                  // When state changes, reset city
                  setForm((prev) => ({
                    ...prev,
                    state: newValue || "",
                    city: "",
                  }));
                  if (validationRules["state"]) {
                    const isValid = validationRules["state"](newValue || "");
                    setErrors((err) => ({
                      ...err,
                      state: isValid === true ? undefined : isValid,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    variant="outlined"
                    name="state"
                    error={!!errors.state}
                    helperText={errors.state || ""}
                    size="medium"
                    autoComplete="off"
                  />
                )}
                fullWidth
                clearOnEscape
              />
            </div>
            <div className="form-half">
              <Autocomplete
                id="city"
                options={cityList}
                getOptionLabel={(option) => option}
                value={form.city || null}
                disabled={!form.state}
                onChange={(_, newValue) => {
                  setForm((prev) => ({ ...prev, city: newValue || "" }));
                  if (validationRules["city"]) {
                    const isValid = validationRules["city"](newValue || "");
                    setErrors((err) => ({
                      ...err,
                      city: isValid === true ? undefined : isValid,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    variant="outlined"
                    name="city"
                    error={!!errors.city}
                    // helperText={
                    //   errors.city || (!form.state ? "Select a state first" : "")
                    // }
                    size="medium"
                    autoComplete="off"
                  />
                )}
                fullWidth
                clearOnEscape
              />
            </div>
          </div>

          <button
            type="submit"
            className="form-submit-button"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          {submitMsg && <div className="form-submit-message">{submitMsg}</div>}
        </form>
      </Container>

      <SuccessModal
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        onNext={async () => {
          setOpenSuccessModal(false);
          if (warrantyChecked) {
            await handleWarrantyUpdate();
          }
          setOpenThankYouModal(true);
        }}
        warrantyChecked={warrantyChecked}
        onWarrantyChange={handleWarrantyCheckbox}
      />

      {openThankYouModal && (
        <ThankYouDialog
          open={openThankYouModal}
          onClose={() => setOpenThankYouModal(false)}
        />
      )}
    </>
  );
};

export default Form;
